import { createWithEqualityFn } from 'zustand/traditional';

import type {
  InventoryCatalogAssortmentListItem,
  InventoryCatalogAssortmentLiveStatus,
  InventoryCatalogLiveEvent,
  InventoryCatalogLiveSnapshot,
  InventoryCatalogSectionLiveStatus,
  InventoryPollerStatus,
  InventoryWarningCode,
} from '../types';

const MAX_EVENTS = 12;
const ERROR_WARNINGS: InventoryWarningCode[] = ['device_offline', 'integration_mismatch', 'reserve_exceeded', 'port_busy'];
const WARNING_WARNINGS: InventoryWarningCode[] = [
  'license_expired',
  'api_unavailable',
  'poller_unavailable',
  'transit_pending',
];
const LIVE_EVENT_HIGHLIGHT_TTL_MS = 4500;
const highlightTimers = new Map<number, number>();

export interface InventoryCatalogLiveState {
  assortments: Record<number, InventoryCatalogAssortmentLiveStatus>;
  sections: Record<number, InventoryCatalogSectionLiveStatus>;
  assortmentToSection: Record<number, number>;
  sectionToAssortments: Record<number, number[]>;
  highlightedAssortmentId: number | null;
  highlightedSectionId: number | null;
  lastEvents: InventoryCatalogLiveEvent[];

  registerAssortments: (items: InventoryCatalogAssortmentListItem[]) => void;
  applyEvent: (event: InventoryCatalogLiveEvent) => void;
  clearHighlights: () => void;
  clearEvents: () => void;
  resolveSectionId: (assortmentId: number) => number | undefined;
  hydrate: (snapshot: InventoryCatalogLiveSnapshot) => void;
  setSectionIndicator: (indicator: InventoryCatalogSectionLiveStatus) => void;
  setAssortmentIndicators: (indicators: InventoryCatalogAssortmentLiveStatus[]) => void;
}

function uniqueWarnings(warnings: InventoryWarningCode[] | undefined): InventoryWarningCode[] {
  if (!warnings?.length) {
    return [];
  }
  return Array.from(new Set(warnings.filter(Boolean)));
}

function mergeWarnings(existing: InventoryWarningCode[] | undefined, incoming: InventoryWarningCode[]): InventoryWarningCode[] {
  if (!existing?.length) {
    return uniqueWarnings(incoming);
  }
  return uniqueWarnings([...existing, ...incoming]);
}

function resolvePollerStatus(status?: InventoryPollerStatus | null): InventoryPollerStatus {
  if (status === 'online' || status === 'offline') {
    return status;
  }
  return 'unknown';
}

function computeSectionStatus(
  sectionId: number,
  assortments: Record<number, InventoryCatalogAssortmentLiveStatus>,
  sectionToAssortments: Record<number, number[]>,
): InventoryCatalogSectionLiveStatus {
  const assortmentIds = sectionToAssortments[sectionId] ?? [];
  if (assortmentIds.length === 0) {
    return {
      sectionId,
      warningCount: 0,
      hasError: false,
      hasWarning: false,
      pollerStatus: 'unknown',
      lastEventAt: undefined,
    };
  }

  let warningCount = 0;
  let hasError = false;
  let hasWarning = false;
  let pollerStatus: InventoryPollerStatus = 'unknown';
  let lastEventAt: string | undefined;

  assortmentIds.forEach((assortmentId) => {
    const indicator = assortments[assortmentId];
    if (!indicator) {
      return;
    }

    const warnings = indicator.warnings ?? [];
    warningCount += warnings.length;
    if (!hasError && warnings.some((warning) => ERROR_WARNINGS.includes(warning))) {
      hasError = true;
    }
    if (!hasWarning && warnings.some((warning) => WARNING_WARNINGS.includes(warning))) {
      hasWarning = true;
    }

    const status = resolvePollerStatus(indicator.pollerStatus);
    if (status === 'offline') {
      pollerStatus = 'offline';
    } else if (status === 'online' && pollerStatus !== 'offline') {
      pollerStatus = 'online';
    }

    if (indicator.generatedAt) {
      if (!lastEventAt || indicator.generatedAt > lastEventAt) {
        lastEventAt = indicator.generatedAt;
      }
    }
  });

  return {
    sectionId,
    warningCount,
    hasError,
    hasWarning: hasWarning || hasError,
    pollerStatus,
    lastEventAt,
  };
}

export const useInventoryCatalogLiveStore = createWithEqualityFn<InventoryCatalogLiveState>()((set, get) => ({
  assortments: {},
  sections: {},
  assortmentToSection: {},
  sectionToAssortments: {},
  highlightedAssortmentId: null,
  highlightedSectionId: null,
  lastEvents: [],

  registerAssortments: (items) =>
    set((state) => {
      if (!items.length) {
        return state;
      }

      const nextAssortmentToSection = { ...state.assortmentToSection };
      const nextSectionToAssortments: Record<number, number[]> = { ...state.sectionToAssortments };
      const touchedSections = new Set<number>();

      items.forEach((item) => {
        nextAssortmentToSection[item.id] = item.inventorySectionId;
        touchedSections.add(item.inventorySectionId);

        const current = nextSectionToAssortments[item.inventorySectionId] ?? [];
        if (!current.includes(item.id)) {
          nextSectionToAssortments[item.inventorySectionId] = [...current, item.id];
        }
      });

      const nextSections = { ...state.sections };
      touchedSections.forEach((sectionId) => {
        nextSections[sectionId] = computeSectionStatus(sectionId, state.assortments, nextSectionToAssortments);
      });

      return {
        ...state,
        assortmentToSection: nextAssortmentToSection,
        sectionToAssortments: nextSectionToAssortments,
        sections: nextSections,
      };
    }),

  applyEvent: (event) =>
    set((state) => {
      const normalizedWarnings = uniqueWarnings(event.warnings);
      const sectionId = event.sectionId ?? state.assortmentToSection[event.assortmentId];

      const nextAssortments = {
        ...state.assortments,
        [event.assortmentId]: {
          assortmentId: event.assortmentId,
          sectionId,
          warnings: mergeWarnings(state.assortments[event.assortmentId]?.warnings, normalizedWarnings),
          source: event.source ?? state.assortments[event.assortmentId]?.source,
          licenseStatus: event.licenseStatus ?? state.assortments[event.assortmentId]?.licenseStatus,
          pollerStatus: event.pollerStatus ?? state.assortments[event.assortmentId]?.pollerStatus,
          pollerMessage: event.pollerMessage ?? state.assortments[event.assortmentId]?.pollerMessage,
          message: event.message ?? state.assortments[event.assortmentId]?.message,
          equipmentId: event.equipmentId ?? state.assortments[event.assortmentId]?.equipmentId,
          generatedAt: event.generatedAt ?? state.assortments[event.assortmentId]?.generatedAt,
        },
      };

      const nextAssortmentToSection = { ...state.assortmentToSection };
      const nextSectionToAssortments = { ...state.sectionToAssortments };
      if (sectionId) {
        nextAssortmentToSection[event.assortmentId] = sectionId;
        const current = new Set(nextSectionToAssortments[sectionId] ?? []);
        current.add(event.assortmentId);
        nextSectionToAssortments[sectionId] = Array.from(current.values());
      }

      const nextSections = { ...state.sections };
      if (sectionId) {
        nextSections[sectionId] = computeSectionStatus(sectionId, nextAssortments, nextSectionToAssortments);
      }

      const eventRecord: InventoryCatalogLiveEvent = {
        ...event,
        sectionId,
        warnings: normalizedWarnings,
      };
      const nextEvents = [eventRecord, ...state.lastEvents].slice(0, MAX_EVENTS);

      if (event.assortmentId) {
        scheduleHighlightClear(event.assortmentId);
      }

      return {
        ...state,
        assortments: nextAssortments,
        sections: nextSections,
        assortmentToSection: nextAssortmentToSection,
        sectionToAssortments: nextSectionToAssortments,
        highlightedAssortmentId: event.assortmentId,
        highlightedSectionId: sectionId ?? null,
        lastEvents: nextEvents,
      };
    }),

  clearHighlights: () =>
    set((state) => {
      clearHighlightTimer(state.highlightedAssortmentId);
      return {
        ...state,
        highlightedAssortmentId: null,
        highlightedSectionId: null,
      };
    }),

  clearEvents: () =>
    set((state) => ({
      ...state,
      lastEvents: [],
    })),

  resolveSectionId: (assortmentId) => {
    const map = get().assortmentToSection;
    return map[assortmentId];
  },

  hydrate: (snapshot) =>
    set((state) => {
      const nextAssortments = { ...state.assortments };
      snapshot.assortments?.forEach((indicator) => {
        nextAssortments[indicator.assortmentId] = {
          ...nextAssortments[indicator.assortmentId],
          ...indicator,
          warnings: uniqueWarnings(indicator.warnings),
        };
      });

      const nextSections = { ...state.sections };
      snapshot.sections?.forEach((indicator) => {
        nextSections[indicator.sectionId] = { ...indicator };
      });

      const normalizedEvents =
        snapshot.events?.length && snapshot.events.length > 0
          ? snapshot.events
              .map((event) => ({
                ...event,
                warnings: uniqueWarnings(event.warnings),
              }))
              .slice(0, MAX_EVENTS)
          : state.lastEvents;

      return {
        ...state,
        assortments: nextAssortments,
        sections: nextSections,
        lastEvents: normalizedEvents,
      };
    }),

  setSectionIndicator: (indicator) =>
    set((state) => ({
      ...state,
      sections: {
        ...state.sections,
        [indicator.sectionId]: { ...indicator },
      },
    })),

  setAssortmentIndicators: (indicators) =>
    set((state) => {
      if (!indicators.length) {
        return state;
      }
      const nextAssortments = { ...state.assortments };
      indicators.forEach((indicator) => {
        nextAssortments[indicator.assortmentId] = {
          ...nextAssortments[indicator.assortmentId],
          ...indicator,
          warnings: uniqueWarnings(indicator.warnings),
        };
      });

      const nextSections = { ...state.sections };
      Object.keys(state.sectionToAssortments).forEach((sectionKey) => {
        const sectionId = Number(sectionKey);
        if (Number.isFinite(sectionId)) {
          nextSections[sectionId] = computeSectionStatus(sectionId, nextAssortments, state.sectionToAssortments);
        }
      });

      return {
        ...state,
        assortments: nextAssortments,
        sections: nextSections,
      };
    }),
}));

function clearHighlightTimer(assortmentId: number | null | undefined): void {
  if (typeof window === 'undefined' || assortmentId == null) {
    return;
  }

  const timerId = highlightTimers.get(assortmentId);
  if (timerId != null) {
    window.clearTimeout(timerId);
    highlightTimers.delete(assortmentId);
  }
}

function scheduleHighlightClear(assortmentId: number): void {
  if (typeof window === 'undefined' || !Number.isFinite(assortmentId)) {
    return;
  }

  clearHighlightTimer(assortmentId);

  const timeoutId = window.setTimeout(() => {
    const { highlightedAssortmentId, clearHighlights } = useInventoryCatalogLiveStore.getState();
    if (highlightedAssortmentId === assortmentId) {
      clearHighlights();
    } else {
      highlightTimers.delete(assortmentId);
    }
  }, LIVE_EVENT_HIGHLIGHT_TTL_MS);

  highlightTimers.set(assortmentId, timeoutId);
}

